// @ts-nocheck
// src/portals/teacher/TeacherAttendanceToday.tsx
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Spinner from "../../components/Spinner";
import ErrorPage from "../../components/ErrorPage";
import Modal from "../../components/Modal";
import { AuthContext } from "../../AuthProvider";
import { usePermissionsCtx } from "../../authz/PermissionsProvider";
import { useCan } from "../../authz/useCan";
import http from "../../api/apiClient";
import { fetchCurrentSchoolYear } from "../../utils/fetchSchoolYears";
import { parseToDate } from "../../utils/dateUtils";
import { translateClassName } from "../../utils/classroomUtils";
import { FaClipboardList } from "react-icons/fa";
import {
  LdsOverviewHero,
  LdsOverviewPage,
} from "../../components/lds/LdsOverview";
import {
  fetchAttendanceRecords,
  upsertAttendanceRecord,
  updateAttendanceRecord,
} from "../../api/attendance";

const attendanceTodayStyles = `
  @keyframes attendanceTodayEnter {
    from {
      opacity: 0;
      transform: translateY(14px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .attendance-today-enter {
    animation: attendanceTodayEnter 420ms cubic-bezier(0.22, 1, 0.36, 1);
    animation-fill-mode: both;
  }

  @media (prefers-reduced-motion: reduce) {
    .attendance-today-enter {
      animation: none !important;
    }
  }
`;

const rolePriority = (role) => {
  const normalized = String(role || "").toLowerCase();
  if (["primary", "mentor", "lead"].includes(normalized)) return 0;
  if (["assistant", "support"].includes(normalized)) return 1;
  return 2;
};

const ABSENCE_STATUSES = ["ABSENT", "EXCUSED"];
const LATE_STATUS = "LATE";

const isAbsenceStatus = (status) => ABSENCE_STATUSES.includes(status);
const isLateStatus = (status) => status === LATE_STATUS;

const normalizeChildId = (child) => {
  if (!child) return null;
  return (
    child.id ??
    child.childId ??
    child.child?.id ??
    child.referenceId ??
    child.childReferenceId ??
    null
  );
};

const childDisplayName = (child) => {
  if (!child) return "-";
  const parts = [
    child.displayName,
    child.fullName,
    [child.firstName, child.middleName, child.lastName].filter(Boolean).join(" "),
    [child.firstName, child.lastName].filter(Boolean).join(" "),
    child.name,
  ];
  return parts.find((value) => value && value.trim().length > 0) ?? "-";
};

const buildNameFromEntity = (entity, visited = new Set(), depth = 0) => {
  if (!entity || typeof entity !== "object" || depth > 3) return null;
  if (visited.has(entity)) return null;
  visited.add(entity);

  const directStrings = [
    entity.teacherName,
    entity.preferredName,
    entity.preferred_name,
    entity.displayName,
    entity.display_name,
    entity.fullName,
    entity.fullname,
    entity.name,
    entity.username,
    entity.email,
    [entity.firstName, entity.middleName, entity.lastName].filter(Boolean).join(" "),
    [entity.first_name, entity.middle_name, entity.last_name].filter(Boolean).join(" "),
  ];
  for (const direct of directStrings) {
    if (typeof direct === "string") {
      const trimmed = direct.trim();
      if (trimmed) return trimmed;
    }
  }

  const nestedKeys = ["teacher", "person", "profile", "user", "contact"];
  for (const key of nestedKeys) {
    const nested = entity[key];
    if (Array.isArray(nested)) {
      for (const item of nested) {
        const nestedName = buildNameFromEntity(item, visited, depth + 1);
        if (nestedName) return nestedName;
      }
    } else {
      const nestedName = buildNameFromEntity(nested, visited, depth + 1);
      if (nestedName) return nestedName;
    }
  }

  return null;
};

const teacherDisplayName = (teacher) => {
  const resolved = buildNameFromEntity(teacher);
  if (resolved) return resolved;
  if (teacher && teacher.id != null) {
    return String(teacher.id);
  }
  return "";
};

const pickPrimaryTeacherEntry = (classroom) => {
  if (!classroom || typeof classroom !== "object") return null;
  const teachers = Array.isArray(classroom.teachers) ? classroom.teachers : [];
  if (teachers.length === 0) return null;
  const now = new Date();
  const normalizeDate = (value) => {
    const parsed = parseToDate(value);
    return parsed ?? null;
  };
  const active = teachers.filter((entry) => {
    const end = normalizeDate(entry?.endDate ?? entry?.end_date);
    return !end || end >= now;
  });
  const entry = active[0] ?? teachers[0];
  return entry || null;
};

const extractTeacherEntity = (teacherEntry) => {
  if (!teacherEntry || typeof teacherEntry !== "object") return null;
  return (
    teacherEntry.teacher ??
    teacherEntry.profile ??
    teacherEntry.person ??
    teacherEntry.user ??
    teacherEntry
  );
};

const mergeChildrenWithRecords = (children, classroomId, records = []) => {
  const normalizeStatus = (value) => (value === "X" ? null : value);
  const recordByChild = new Map();
  records.forEach((record) => {
    const recordChildId =
      record?.childId ??
      record?.child?.id ??
      record?.child?.childId ??
      record?.child?.child?.id ??
      null;
    if (recordChildId) {
      recordByChild.set(recordChildId, record);
    }
  });

  const students = [];
  children.forEach((child) => {
    const childId = normalizeChildId(child);
    if (!childId) return;
    const existing = recordByChild.get(childId);
    students.push({
      childId,
      name: childDisplayName(child),
      recordId: existing?.id ?? null,
      classroomId: existing?.classroomId ?? classroomId,
      status: normalizeStatus(existing?.status) ?? null,
      child,
    });
  });

  return students.sort((a, b) => a.name.localeCompare(b.name));
};

const TeacherAttendanceToday = () => {
  const { t } = useTranslation();
  const { keycloak, kcReady } = useContext(AuthContext);
  const { ready: permissionsReady } = usePermissionsCtx();

  const canMarkAttendance = useCan("school.attendance", "manage");
  const canManageAttendance = useCan("school.attendance", "manage");
  const hasAttendanceAccess = canMarkAttendance || canManageAttendance;

  const teacherSub = keycloak?.tokenParsed?.sub || null;
  const defaultTodayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);

  const [teacher, setTeacher] = useState(null);
  const [schoolYear, setSchoolYear] = useState(null);
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [classroom, setClassroom] = useState(null);
  const [students, setStudents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [openReasonFor, setOpenReasonFor] = useState(null);
  const [selectedDate, setSelectedDate] = useState(defaultTodayIso);
  const [showAlreadySubmittedModal, setShowAlreadySubmittedModal] = useState(false);
  const [alternativeDate, setAlternativeDate] = useState(defaultTodayIso);
  const hasLoadedRef = useRef(false);
  const lastLoadedDateRef = useRef(defaultTodayIso);

  useEffect(() => {
    if (alreadySubmitted || saving) {
      setOpenReasonFor(null);
    }
  }, [alreadySubmitted, saving]);

  useEffect(() => {
    if (showAlreadySubmittedModal) {
      setAlternativeDate(selectedDate);
    }
  }, [showAlreadySubmittedModal, selectedDate]);

  const presentCount = useMemo(
    () => students.filter((student) => student.status === "PRESENT").length,
    [students]
  );
  const absentCount = useMemo(
    () => students.filter((student) => isAbsenceStatus(student.status)).length,
    [students]
  );
  const lateCount = useMemo(
    () => students.filter((student) => isLateStatus(student.status)).length,
    [students]
  );
  const unmarkedCount = useMemo(
    () => students.filter((student) => !student.status).length,
    [students]
  );
  const allPresent = useMemo(
    () => students.length > 0 && students.every((student) => student.status === "PRESENT"),
    [students]
  );
  const isSubmitDisabled = useMemo(
    () =>
      saving ||
      alreadySubmitted ||
      !currentAssignment?.classroomId ||
      students.length === 0 ||
      unmarkedCount > 0,
    [alreadySubmitted, currentAssignment?.classroomId, saving, students.length, unmarkedCount]
  );
  const isMarkAllDisabled = useMemo(
    () => saving || alreadySubmitted || students.length === 0,
    [saving, alreadySubmitted, students.length]
  );
  const absenceOptions = useMemo(
    () => [
      {
        value: "ABSENT",
        label: t("attendance.status.absent", { defaultValue: "Niet aanwezig" }),
      },
      {
        value: "EXCUSED",
        label: t("attendance.status.excused", { defaultValue: "Geoorloofd afwezig" }),
      },
      {
        value: LATE_STATUS,
        label: t("attendance.status.late", { defaultValue: "Te laat" }),
      },
    ],
    [t]
  );

  const splitAssignments = useCallback((rows) => {
    const now = new Date();
    const groups = { past: [], now: [], future: [], unknown: [] };

    rows.forEach((row) => {
      const start = parseToDate(row.startDate);
      const end = parseToDate(row.endDate);
      if (!start && !end) {
        groups.unknown.push(row);
        return;
      }
      if (end && end < now) {
        groups.past.push(row);
        return;
      }
      if (start && start > now) {
        groups.future.push(row);
        return;
      }
      groups.now.push(row);
    });

    return {
      past: groups.past.sort(
        (a, b) => (parseToDate(b.startDate)?.getTime() ?? 0) - (parseToDate(a.startDate)?.getTime() ?? 0)
      ),
      now: groups.now.sort((a, b) => rolePriority(a.role) - rolePriority(b.role)),
      future: groups.future.sort(
        (a, b) => (parseToDate(a.startDate)?.getTime() ?? 0) - (parseToDate(b.startDate)?.getTime() ?? 0)
      ),
      unknown: groups.unknown,
    };
  }, []);

  const pickPreferredAssignment = useCallback((groups, currentYear) => {
    const overlap = (assignment) => {
      if (!currentYear) return true;
      const yearStart = parseToDate(currentYear.startDate);
      const yearEnd = parseToDate(currentYear.endDate);
      const start = parseToDate(assignment.startDate) ?? yearStart;
      const end = parseToDate(assignment.endDate) ?? yearEnd;
      if (yearStart && end && end < yearStart) return false;
      if (yearEnd && start && start > yearEnd) return false;
      return true;
    };

    const candidates = [groups.now, groups.future, groups.past, groups.unknown]
      .reduce((acc, list) => acc.concat(list), [])
      .filter(overlap);

    if (!candidates.length) return null;

    return candidates
      .slice()
      .sort((a, b) => {
        const roleDiff = rolePriority(a.role) - rolePriority(b.role);
        if (roleDiff !== 0) return roleDiff;
        const aStart = parseToDate(a.startDate)?.getTime() ?? 0;
        const bStart = parseToDate(b.startDate)?.getTime() ?? 0;
        return bStart - aStart;
      })[0];
  }, []);


  const loadChildren = useCallback(
    async (classroomId, targetDate) => {
      if (!classroomId) {
        setClassroom(null);
        setStudents([]);
        setAlreadySubmitted(false);
        setShowAlreadySubmittedModal(false);
        lastLoadedDateRef.current = targetDate || defaultTodayIso;
        return;
      }

      const effectiveDate = targetDate || defaultTodayIso;
      setAlreadySubmitted(false);
      setShowAlreadySubmittedModal(false);
      setOpenReasonFor(null);
      try {
        const classroomRes = await http.get(`/classrooms/${classroomId}`);
        const classroomData = classroomRes?.data ?? null;
        setClassroom(classroomData);

        if (classroomData) {
          const teacherEntry = pickPrimaryTeacherEntry(classroomData);
          if (teacherEntry) {
            let candidate = extractTeacherEntity(teacherEntry);
            if (!teacherDisplayName(candidate) && teacherEntry.teacherId) {
              try {
                const teacherRes = await http.get(`/teachers/${teacherEntry.teacherId}`);
                if (teacherRes?.data) {
                  candidate = teacherRes.data;
                }
              } catch (resolveErr) {
                // ignore, fallback to entry data
              }
            }
            if (candidate) {
              const candidateName = teacherDisplayName(candidate);
              setTeacher((prev) => {
                const prevName = teacherDisplayName(prev);
                if (!candidateName) {
                  return prev ?? candidate;
                }
                if (!prevName) {
                  return candidate;
                }
                const candidateId = teacherEntry.teacherId ?? candidate.id ?? null;
                if (
                  prevName === String(candidateId ?? "") &&
                  candidateName !== prevName
                ) {
                  return candidate;
                }
                return prev;
              });
            }
          }
        }

        let attendanceRecords = [];
        try {
          attendanceRecords = await fetchAttendanceRecords({
            classroomId,
            date: effectiveDate,
          });
        } catch (attendanceErr) {
          if (attendanceErr?.response?.status === 404) {
            attendanceRecords = [];
          } else {
            throw attendanceErr;
          }
        }

        const childrenRes = await http.get(`/classrooms/${classroomId}/children`, {
          validateStatus: (status) => [200, 204, 404].includes(status),
        });

        if (childrenRes.status === 200 && Array.isArray(childrenRes.data)) {
          const merged = mergeChildrenWithRecords(childrenRes.data, classroomId, attendanceRecords);
          setStudents(merged);
          if (!merged.length) {
            setFeedback({
              type: "info",
              message: t("attendance.today.no_students", {
                defaultValue: "Geen leerlingen gevonden voor deze klas.",
              }),
            });
          } else {
            setFeedback(null);
          }
        } else if (childrenRes.status === 204 || childrenRes.status === 404) {
          setStudents([]);
          setFeedback({
            type: "info",
            message: t("attendance.today.no_students", {
              defaultValue: "Geen leerlingen gevonden voor deze klas.",
            }),
          });
        } else {
          setStudents([]);
          setFeedback({
            type: "error",
            message: t("attendance.today.load_failed", {
              defaultValue: "Kon de aanwezigheden voor vandaag niet ophalen.",
            }),
          });
        }

        const hasExistingRecords =
          Array.isArray(attendanceRecords) && attendanceRecords.length > 0;
        setAlreadySubmitted(hasExistingRecords);
        setShowAlreadySubmittedModal(hasExistingRecords);
        lastLoadedDateRef.current = effectiveDate;

      } catch (err) {
        setClassroom(null);
        setStudents([]);
        setAlreadySubmitted(false);
        setShowAlreadySubmittedModal(false);
        setFeedback({
          type: "error",
          message:
            err?.response?.status === 403
              ? t("error.403.message")
              : t("attendance.today.load_failed", {
                  defaultValue: "Kon de aanwezigheden voor vandaag niet ophalen.",
                }),
        });
      }
    },
    [t, defaultTodayIso]
  );

  useEffect(() => {
    if (!permissionsReady || !kcReady) return;
    if (!hasAttendanceAccess) return;

    if (hasLoadedRef.current) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setFeedback(null);

      try {
        const currentYear = await fetchCurrentSchoolYear();
        if (cancelled) return;
        setSchoolYear(currentYear);

        if (!teacherSub && !canManageAttendance) {
          setFeedback({
            type: "error",
            message: t("attendance.today.no_teacher_linked", {
              defaultValue: "Geen docentkoppeling gevonden voor dit account.",
            }),
          });
          setClassroom(null);
          setStudents([]);
          return;
        }

        let teacherRecord = teacher;
        if (!canManageAttendance && !teacherRecord) {
          try {
            const { data: list } = await http.get("/teachers");
            if (cancelled) return;
            const teacherList = Array.isArray(list) ? list : [];
            teacherRecord = teacherList.find((item) => item?.keycloakUserId === teacherSub) || null;
            if (teacherRecord) {
              setTeacher(teacherRecord);
            }
          } catch (fetchErr) {
            if (cancelled) return;
            if (fetchErr?.response?.status !== 403) {
              throw fetchErr;
            }
          }
        }

        let rows = [];
        if (teacherRecord?.id) {
          try {
            const { data } = await http.get(`/teacher-classrooms/teacher/${teacherRecord.id}`);
            if (cancelled) return;
            rows = Array.isArray(data) ? data : [];
          } catch (assignErr) {
            if (cancelled) return;
            if (assignErr?.response?.status !== 403) {
              throw assignErr;
            }
          }
        }

        if (rows.length === 0) {
          try {
            const { data } = await http.get("/teacher-classrooms/current");
            if (cancelled) return;
            rows = Array.isArray(data) ? data : [];
            if (!teacherRecord) {
              const fallbackTeacher =
                rows.find((row) => row?.teacher)?.teacher ?? null;
              if (fallbackTeacher) {
                setTeacher(fallbackTeacher);
                teacherRecord = fallbackTeacher;
              }
            }
          } catch (currentErr) {
            if (cancelled) return;
            if (currentErr?.response?.status !== 403) {
              throw currentErr;
            }
          }
        }

        if (rows.length === 0) {
          setFeedback({
            type: "error",
            message: t("attendance.today.no_teacher_linked", {
              defaultValue: "Geen docentkoppeling gevonden voor dit account.",
            }),
          });
          setClassroom(null);
          setStudents([]);
          return;
        }

        const grouped = splitAssignments(rows);
        const preferred = pickPreferredAssignment(grouped, currentYear);
        if (cancelled) return;

        setCurrentAssignment(preferred);
        await loadChildren(preferred?.classroomId ?? null, selectedDate);
      } catch (err) {
        if (cancelled) return;
        setFeedback({
          type: "error",
          message:
            err?.response?.status === 403
              ? t("error.403.message")
              : t("attendance.today.load_failed", {
                  defaultValue: "Kon docentgegevens niet ophalen.",
                }),
        });
        setClassroom(null);
        setStudents([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
          hasLoadedRef.current = true;
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    permissionsReady,
    kcReady,
    hasAttendanceAccess,
    canManageAttendance,
    teacherSub,
    splitAssignments,
    pickPreferredAssignment,
    loadChildren,
    t,
    selectedDate,
  ]);

  const handleStatusChange = (childId, status) => {
    setStudents((prev) =>
      prev.map((student) => {
        if (student.childId !== childId) return student;
        return { ...student, status };
      })
    );
    setFeedback(null);
    setOpenReasonFor(null);
  };
  const handleToggleReasonMenu = useCallback(
    (childId) => {
      if (alreadySubmitted || saving) return;
      setOpenReasonFor((prev) => (prev === childId ? null : childId));
    },
    [alreadySubmitted, saving]
  );
  const handleSelectReason = (childId, status) => {
    if (alreadySubmitted || saving) return;
    handleStatusChange(childId, status);
    setOpenReasonFor(null);
  };
  const handleMarkAllPresent = useCallback(() => {
    if (isMarkAllDisabled) return;
    setStudents((prev) =>
      prev.map((student) => ({ ...student, status: allPresent ? null : "PRESENT" }))
    );
    setOpenReasonFor(null);
    setFeedback(null);
  }, [isMarkAllDisabled, allPresent]);

  useEffect(() => {
    if (!hasAttendanceAccess) return;
    if (!currentAssignment?.classroomId) return;
    if (!hasLoadedRef.current) return;
    if (lastLoadedDateRef.current === selectedDate) return;
    loadChildren(currentAssignment.classroomId, selectedDate);
  }, [hasAttendanceAccess, currentAssignment?.classroomId, selectedDate, loadChildren]);

  if (!permissionsReady || !kcReady) {
    return <Spinner />;
  }

  const handleSubmit = async () => {
    if (saving || alreadySubmitted || !currentAssignment?.classroomId) return;
    if (students.some((student) => !student.status)) {
      setFeedback({
        type: "error",
        message: t("attendance.today.missing_status", { defaultValue: "Selecteer voor alle leerlingen de aanwezigheid." }),
      });
      return;
    }

    setSaving(true);
    setFeedback(null);

    try {
      const entries = students.map((student) => ({
        recordId: student.recordId ?? null,
        classroomId: student.classroomId ?? currentAssignment.classroomId,
        childId: student.childId,
        attendanceDate: selectedDate,
        status: student.status ?? "X",
      }));

      const savedRecords = [];
      for (const entry of entries) {
        const { recordId, ...payload } = entry;
        if (recordId) {
          const updated = await updateAttendanceRecord(recordId, payload);
          savedRecords.push(updated);
        } else {
          const created = await upsertAttendanceRecord(payload);
          savedRecords.push(created);
        }
      }

      setStudents((prev) =>
        prev.map((student) => {
          const saved = savedRecords.find(
            (record) => (record?.childId ?? record?.child?.id ?? null) === student.childId
          );
          if (!saved) return student;
          return {
            ...student,
            recordId: saved.id ?? student.recordId,
            status: saved.status === "X" ? null : saved.status ?? student.status,
          };
        })
      );
      setAlreadySubmitted(true);
      setFeedback({
        type: "success",
        message: t("attendance.today.save_success", { defaultValue: "Aanwezigheid opgeslagen." }),
      });
    } catch (err) {
      setFeedback({
        type: "error",
        message:
          err?.response?.status === 403
            ? t("error.403.message")
            : t("attendance.today.save_failed", { defaultValue: "Opslaan is niet gelukt." }),
      });
    } finally {
      setSaving(false);
    }
  };

  if (!hasAttendanceAccess) {
    return (
      <ErrorPage
        status={403}
        message={t("error.403.message")}
        onRetry={() => window.location.reload()}
      />
    );
  }

  if (loading) {
    return (
      <LdsOverviewPage styles={<style>{attendanceTodayStyles}</style>}>
        <LdsOverviewHero
          icon={FaClipboardList}
          eyebrow={t("attendance.today.eyebrow", { defaultValue: "Attendance" })}
          title={t("attendance.today.title", { defaultValue: "Aanwezigheid vandaag" })}
          description={t("attendance.today.description", {
            defaultValue: "Mark attendance for your classroom.",
          })}
        />
        <div className="attendance-today-enter flex justify-center py-10">
          <Spinner />
        </div>
      </LdsOverviewPage>
    );
  }

  return (
    <>
      <LdsOverviewPage styles={<style>{attendanceTodayStyles}</style>}>
        <LdsOverviewHero
          icon={FaClipboardList}
          eyebrow={t("attendance.today.eyebrow", { defaultValue: "Attendance" })}
          title={t("attendance.today.title", { defaultValue: "Aanwezigheid vandaag" })}
          description={t("attendance.today.description", {
            defaultValue: "Mark attendance for your classroom.",
          })}
        />
        <div className="space-y-6">
        {feedback && (
          <div
            className={`attendance-today-enter rounded-2xl border px-4 py-3 text-sm font-semibold shadow-sm ${
              feedback.type === "success"
                ? "border-green-300 bg-green-50 text-green-700 dark:border-green-500/60 dark:bg-green-500/10 dark:text-green-200"
                : feedback.type === "error"
                ? "border-red-300 bg-red-50 text-red-700 dark:border-red-500/60 dark:bg-red-500/10 dark:text-red-200"
                : "border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-500/60 dark:bg-blue-500/10 dark:text-blue-200"
            }`}
          >
            {feedback.message}
          </div>
        )}

        <div className="attendance-today-enter relative rounded-[28px] border border-slate-200/80 bg-white/95 p-6 shadow-[0_14px_35px_rgba(15,23,42,0.07)] dark:border-slate-800 dark:bg-slate-950">
          <h2 className="text-xl font-semibold text-indigo-700 dark:text-indigo-300">
            {t("attendance.today.teacher_overview", { defaultValue: "Docent" })}
          </h2>
          <p className="mt-2 text-gray-800 dark:text-gray-100">
            {teacher
              ? teacherDisplayName(teacher) ||
                t("attendance.today.no_teacher_linked", {
                  defaultValue: "Geen docentkoppeling gevonden voor dit account.",
                })
              : t("attendance.today.no_teacher_linked", {
                  defaultValue: "Geen docentkoppeling gevonden voor dit account.",
                })}
          </p>
          {schoolYear?.label && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t("attendance.admin.school_year", { defaultValue: "Schooljaar" })}: {schoolYear.label}
            </p>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
            <span className="rounded bg-green-100 px-3 py-1 text-green-700 dark:bg-green-500/10 dark:text-green-200">
              {t("attendance.today.present_count", {
                defaultValue: "{{count}} aanwezig",
                count: presentCount,
              })}
            </span>
            <span className="rounded bg-red-100 px-3 py-1 text-red-700 dark:bg-red-500/10 dark:text-red-200">
              {t("attendance.today.absent_count", {
                defaultValue: "{{count}} afwezig",
                count: absentCount,
              })}
            </span>
            <span className="rounded bg-purple-100 px-3 py-1 text-purple-700 dark:bg-purple-500/10 dark:text-purple-200">
              {t("attendance.today.late_count", {
                defaultValue: "{{count}} te laat",
                count: lateCount,
              })}
            </span>
            <span className="rounded bg-yellow-100 px-3 py-1 text-yellow-700 dark:bg-yellow-500/10 dark:text-yellow-200">
              {t("attendance.today.unmarked_count", {
                defaultValue: "{{count}} open",
                count: unmarkedCount,
              })}
            </span>
            <span className="rounded bg-indigo-100 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-200">
              {t("attendance.today.selected_date", {
                defaultValue: "Datum: {{date}}",
                date: new Date(selectedDate).toLocaleDateString(),
              })}
            </span>
          </div>

        </div>

        <div className="attendance-today-enter relative rounded-[28px] border border-slate-200/80 bg-gradient-to-br from-sky-50/80 via-white to-indigo-50/70 p-6 shadow-[0_14px_35px_rgba(15,23,42,0.07)] dark:border-slate-800 dark:from-slate-950 dark:via-slate-950 dark:to-indigo-950/30">
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">
            {translateClassName(classroom) || currentAssignment?.classroomId}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {classroom?.startDate
              ? new Date(classroom.startDate).toLocaleDateString()
              : t("attendance.today.assignment_start", { defaultValue: "Start" })}{" "}
            –{" "}
            {classroom?.endDate
              ? new Date(classroom.endDate).toLocaleDateString()
              : "—"}
          </p>

          {students.length === 0 ? (
            <div className="mt-4 rounded border border-dashed px-4 py-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
              {t("attendance.today.no_students", {
                defaultValue: "Geen leerlingen gevonden voor deze klas.",
              })}
            </div>
          ) : (
            <>
              <div className="mt-4 grid gap-5 md:grid-cols-2">
                {students.map((student) => {
                  const cardAccent =
                    student.status === "PRESENT"
                      ? "border-emerald-200/70 bg-white/90 shadow-[0_25px_50px_rgba(16,185,129,0.18)] dark:border-emerald-500/50 dark:bg-emerald-500/10 dark:shadow-none"
                      : isLateStatus(student.status)
                      ? "border-purple-200/70 bg-white/85 shadow-[0_25px_50px_rgba(147,51,234,0.18)] dark:border-purple-500/50 dark:bg-purple-500/10 dark:shadow-none"
                      : isAbsenceStatus(student.status)
                      ? "border-red-200/70 bg-white/85 shadow-[0_25px_50px_rgba(239,68,68,0.18)] dark:border-red-500/50 dark:bg-red-500/10 dark:shadow-none"
                      : "border-white/60 bg-white/80 shadow-[0_25px_60px_rgba(15,23,42,0.12)] dark:border-gray-700 dark:bg-gray-900/40 dark:shadow-none";
                  const absenceButtonClasses = (() => {
                    if (student.status === "ABSENT") return "bg-red-600 text-white shadow dark:bg-red-500";
                    if (student.status === "EXCUSED") return "bg-yellow-600 text-white shadow dark:bg-yellow-500";
                    if (isLateStatus(student.status)) return "bg-purple-600 text-white shadow dark:bg-purple-500";
                    return "bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-500/10 dark:text-red-200 dark:hover:bg-red-500/20";
                  })();
                  const absenceLabel =
                    absenceOptions.find((option) => option.value === student.status)?.label ||
                    t("attendance.today.mark_absent", { defaultValue: "Niet aanwezig" });
                  const menuOpen = openReasonFor === student.childId;
                  return (
                    <div
                      key={student.childId}
                      className={`attendance-today-enter rounded-2xl border p-4 backdrop-blur-sm transition ${cardAccent}`}
                    >
                      <div className="flex items-center gap-3">
                        <h4 className="flex-1 text-lg font-medium text-gray-800 dark:text-gray-100">{student.name}</h4>
                        <button
                          type="button"
                          className={`rounded px-3 py-2 text-sm font-semibold transition ${
                            student.status === "PRESENT"
                              ? "bg-green-600 text-white shadow dark:bg-green-500"
                              : "bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-500/10 dark:text-green-200 dark:hover:bg-green-500/20"
                          } ${alreadySubmitted ? "opacity-50 cursor-not-allowed" : ""}`}
                          disabled={alreadySubmitted}
                          onClick={() => handleStatusChange(student.childId, "PRESENT")}
                        >
                          {t("attendance.today.mark_present", { defaultValue: "Aanwezig" })}
                        </button>
                        <div className="relative">
                          <button
                            type="button"
                            className={`rounded px-3 py-2 text-sm font-semibold transition ${absenceButtonClasses} ${
                              alreadySubmitted ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                            disabled={alreadySubmitted}
                            onClick={() => handleToggleReasonMenu(student.childId)}
                          >
                            {absenceLabel}
                          </button>
                          {menuOpen && (
                            <div className="absolute right-0 z-10 mt-2 w-52 overflow-hidden rounded-2xl border border-indigo-100/70 bg-white/95 shadow-[0_20px_45px_rgba(15,23,42,0.25)] backdrop-blur dark:border-indigo-500/50 dark:bg-gray-900/95 dark:shadow-black/50">
                              {absenceOptions.map((option) => {
                                const active = option.value === student.status;
                                return (
                                  <button
                                    type="button"
                                    key={option.value}
                                    className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition hover:bg-indigo-50 dark:hover:bg-indigo-500/10 ${
                                      active
                                        ? "font-semibold text-indigo-700 dark:text-indigo-300"
                                        : "text-gray-700 dark:text-gray-200"
                                    }`}
                                    onClick={() => handleSelectReason(student.childId, option.value)}
                                  >
                                    <span>{option.label}</span>
                                    {active && (
                                      <span className="text-xs uppercase text-indigo-500 dark:text-indigo-200">
                                        {t("attendance.today.reason_selected", { defaultValue: "Gekozen" })}
                                      </span>
                                    )}
                                  </button>
                                );
                              })}
                              <button
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm text-gray-600 transition hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
                                onClick={() => handleSelectReason(student.childId, null)}
                              >
                                {t("attendance.today.clear_reason", { defaultValue: "Zet op niet gekozen" })}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-end">
                {unmarkedCount > 0 && (
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    {t("attendance.today.complete_all", {
                      defaultValue: "Markeer alle leerlingen voordat je opslaat.",
                    })}
                  </p>
                )}
                <button
                  type="button"
                  className={`inline-flex items-center justify-center rounded border border-indigo-600 px-4 py-2 text-sm font-semibold text-indigo-600 transition hover:bg-indigo-50 focus:outline-none focus:ring focus:ring-indigo-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-400 dark:text-indigo-300 dark:hover:bg-indigo-500/10 dark:focus:ring-indigo-400 dark:focus:ring-offset-gray-900 ${
                    saving ? "cursor-wait" : ""
                  }`}
                  disabled={isMarkAllDisabled}
                  onClick={handleMarkAllPresent}
                >
                  {allPresent
                    ? t("attendance.today.reset_all", {
                        defaultValue: "Zet iedereen op niet gekozen",
                      })
                    : t("attendance.today.mark_all_present", {
                        defaultValue: "Markeer iedereen aanwezig",
                      })}
                </button>
                <button
                  type="button"
                  className={`inline-flex items-center justify-center rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring focus:ring-indigo-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400 dark:focus:ring-indigo-400 dark:focus:ring-offset-gray-900 ${
                    saving ? "cursor-wait" : ""
                  }`}
                  disabled={isSubmitDisabled}
                  onClick={handleSubmit}
                >
                  {saving
                    ? t("attendance.today.saving", { defaultValue: "Bezig met opslaan..." })
                    : alreadySubmitted
                    ? t("attendance.today.saved", { defaultValue: "Aanwezigheid opgeslagen" })
                    : t("attendance.today.save_button", { defaultValue: "Aanwezigheid opslaan" })}
                </button>
              </div>
            </>
          )}
        </div>

        </div>
      </LdsOverviewPage>

      {showAlreadySubmittedModal && (
        <Modal
          show={showAlreadySubmittedModal}
          title={t("attendance.today.already_submitted_modal.title", {
            defaultValue: "Aanwezigheid al geregistreerd",
          })}
          type="info"
          hideDefaultActions
          dialogClassName="w-[min(92vw,34rem)] max-w-none rounded-[28px] border border-slate-200/80 bg-white/95 p-6 text-left shadow-[0_28px_80px_rgba(15,23,42,0.18)] dark:border-slate-800 dark:bg-slate-950"
          onClose={() => {
            setShowAlreadySubmittedModal(false);
            setOpenReasonFor(null);
          }}
        >
          <div className="attendance-today-enter">
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
              {t("attendance.today.already_submitted_modal.body", {
                defaultValue:
                  "Voor {{date}} is al een aanwezigheidsregistratie opgeslagen. Je kunt de bestaande registratie bewerken of voor een andere dag invullen.",
                date: new Date(selectedDate).toLocaleDateString(),
              })}
            </p>

            <div className="mt-4 space-y-3">
              <label className="flex flex-col space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                  {t("attendance.today.already_submitted_modal.pick_other", {
                    defaultValue: "Andere datum kiezen",
                  })}
                </span>
                <input
                  type="date"
                  className="rounded border px-3 py-2 bg-white text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                  value={alternativeDate}
                  onChange={(e) => setAlternativeDate(e.target.value)}
                />
              </label>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                onClick={() => {
                  setShowAlreadySubmittedModal(false);
                  setOpenReasonFor(null);
                }}
              >
                {t("common.close", { defaultValue: "Sluiten" })}
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded border border-indigo-500 px-4 py-2 text-sm font-semibold text-indigo-600 transition hover:bg-indigo-50 dark:border-indigo-400 dark:text-indigo-300 dark:hover:bg-indigo-500/10"
                onClick={() => {
                  setAlreadySubmitted(false);
                  setShowAlreadySubmittedModal(false);
                  setOpenReasonFor(null);
                }}
              >
                {t("attendance.today.already_submitted_modal.edit_today", {
                  defaultValue: "Bewerk registratie voor deze dag",
                })}
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-400"
                disabled={!alternativeDate}
                onClick={() => {
                  if (!alternativeDate) return;
                  setShowAlreadySubmittedModal(false);
                  setOpenReasonFor(null);
                  setAlreadySubmitted(false);
                  setSelectedDate(alternativeDate);
                }}
              >
                {t("attendance.today.already_submitted_modal.use_other_date", {
                  defaultValue: "Gebruik andere datum",
                })}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default TeacherAttendanceToday;
